import { IFrameworkTemplateStatus, type IRatingMatrixFrameworkTemplate } from '__generated__';
import { createFilterCollectionContains, createFilterContains } from 'utils/dynamic-filters';
import type {
  FrameworkAggregateStatus,
  FrameworkKindFilter,
  FrameworkStatusFilter,
  IRatingMatrixFrameworkListItem,
  SectorCard,
} from './models';
import { normalizeMatrixKind } from './matrix-kind';

export function buildFrameworkSearchFilter(search: string): string | null {
  const trimmed = search.trim();
  if (!trimmed) {
    return null;
  }

  const parts = [
    createFilterContains<IRatingMatrixFrameworkListItem>('title', trimmed),
    createFilterContains<IRatingMatrixFrameworkListItem>('methodology.title', trimmed),
    createFilterCollectionContains('templates', 'title', trimmed),
  ].filter((part): part is string => Boolean(part));

  if (parts.length === 0) {
    return null;
  }

  return parts.length === 1 ? parts[0] : `(${parts.join(' || ')})`;
}

export function getActiveTemplates(framework: IRatingMatrixFrameworkListItem) {
  return (framework.templates ?? []).filter((template) => !template.isArchived);
}

export function isTemplateDraft(status: IFrameworkTemplateStatus | null | undefined): boolean {
  return status === IFrameworkTemplateStatus.Value0;
}

export function isTemplateActive(status: IFrameworkTemplateStatus | null | undefined): boolean {
  return status === IFrameworkTemplateStatus.Value1;
}

export function isTemplateLocked(status: IFrameworkTemplateStatus | null | undefined): boolean {
  return status === IFrameworkTemplateStatus.Value2;
}

export function countNonArchivedLockedTemplates(
  templates: IRatingMatrixFrameworkTemplate[],
): number {
  return templates.filter(
    (tpl) => !tpl.isArchived && isTemplateLocked(tpl.status),
  ).length;
}

/** True when any non-archived template is not a draft (UI locked / live). */
export function hasLiveOrLockedVersion(
  templates: IRatingMatrixFrameworkTemplate[],
): boolean {
  return templates.some((tpl) => !tpl.isArchived && !isTemplateDraft(tpl.status));
}

export function isArchiveBlockedLastLockedVersion(
  target: IRatingMatrixFrameworkTemplate,
  templates: IRatingMatrixFrameworkTemplate[],
): boolean {
  return (
    isTemplateLocked(target.status) &&
    !target.isArchived &&
    countNonArchivedLockedTemplates(templates) === 1
  );
}

export function getFrameworkAggregateStatus(
  framework: IRatingMatrixFrameworkListItem,
): FrameworkAggregateStatus {
  const hasDraft = getActiveTemplates(framework).some((template) =>
    isTemplateDraft(template.status),
  );

  return hasDraft ? 'draft' : 'locked';
}

export function matchesStatusFilter(
  framework: IRatingMatrixFrameworkListItem,
  status: FrameworkStatusFilter,
): boolean {
  if (status === 'all') {
    return true;
  }

  return getFrameworkAggregateStatus(framework) === status;
}

export function matchesSectorFilter(
  framework: IRatingMatrixFrameworkListItem,
  selectedSectorId: number | null,
): boolean {
  if (selectedSectorId === null) {
    return true;
  }

  return framework.methodology?.sector?.id === selectedSectorId;
}

export function matchesKindFilter(
  framework: IRatingMatrixFrameworkListItem,
  kind: FrameworkKindFilter,
): boolean {
  if (kind === 'all') {
    return true;
  }

  return normalizeMatrixKind(framework.matrixKind) === Number(kind);
}

export function buildSectorCards(frameworks: IRatingMatrixFrameworkListItem[]): SectorCard[] {
  const sectors = new Map<number, SectorCard>();

  frameworks.forEach((framework) => {
    const sector = framework.methodology?.sector;
    if (!sector?.id) {
      return;
    }

    const existing = sectors.get(sector.id) ?? {
      sectorId: sector.id,
      title: sector.title ?? '',
      count: 0,
      hasDraft: false,
    };

    if (!framework.isArchived) {
      existing.count += 1;
    }

    const hasDraftTemplate = getActiveTemplates(framework).some((template) =>
      isTemplateDraft(template.status),
    );

    if (hasDraftTemplate) {
      existing.hasDraft = true;
    }

    sectors.set(sector.id, existing);
  });

  return Array.from(sectors.values()).sort((left, right) =>
    left.title.localeCompare(right.title, 'he'),
  );
}
